"use client";

import { useEffect, useState } from "react";
import { API_BASE } from "@/components/constants/general";

type ItemPricing = {
  /**
   * Actual device price the customer pays (items `selling_price` =
   * item_cost − discount_price, e.g. 4999 − 500 = 4499). `null` until loaded.
   */
  itemCost: number | null;
  /** Original price / MRP (`item_cost`, e.g. 4999). `null` until loaded. */
  mrp: number | null;
  /** Flat discount amount (`discount_price`, e.g. 500). 0 when there is no discount. */
  discountAmount: number;
  /** Pre-booking amount from check-availability (e.g. 99). `null` until loaded. */
  prebookingPrice: number | null;
  /** Balance due on delivery = itemCost − prebookingPrice. `null` until both load. */
  remaining: number | null;
};

/**
 * Selling price (₹4,999 MRP − ₹500 launch discount) shown while the API
 * price is loading or the backend is unreachable. Display only — the amount
 * actually charged always comes from the backend.
 */
export const FALLBACK_SELLING_PRICE = 4499;

/**
 * Selling price for an item row from `GET /api/item`. Prefers the backend's
 * `selling_price` (the amount actually charged) and falls back to
 * item_cost − discount_price for older API responses.
 */
export function getSellingPrice(item: any): number {
  const cost = Number(item?.item_cost) || 0;
  const selling = Number(item?.selling_price);
  if (selling > 0) return selling;

  const discount = Number(item?.discount_price) || 0;
  return discount > 0 && discount < cost ? cost - discount : cost;
}

/**
 * Fetches device pricing from the backend for the prebook pages:
 *  - `item_cost` / `discount_price` / `selling_price` via `GET /api/item`
 *  - `prebooking_price` via `GET /api/prebooking/check-availability`
 * `remaining` is derived (selling price − prebooking_price). Values stay `null`
 * until loaded / on failure, so callers can fall back to their static text.
 */
export function useItemPricing(): ItemPricing {
  const [itemCost, setItemCost] = useState<number | null>(null);
  const [mrp, setMrp] = useState<number | null>(null);
  const [prebookingPrice, setPrebookingPrice] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPricing = async () => {
      try {
        const itemRes = await fetch(`${API_BASE}/api/item`, {
          headers: { Accept: "application/json" },
        });
        const itemData = await itemRes.json();

        let item: any = null;
        if (itemData?.status && itemData.data) {
          item = Array.isArray(itemData.data) ? itemData.data[0] : itemData.data;
        } else if (Array.isArray(itemData)) {
          item = itemData[0];
        } else if (itemData?.id) {
          item = itemData;
        }

        const cost = Number(item?.item_cost);
        if (!cancelled && cost > 0) {
          setMrp(cost);
          setItemCost(getSellingPrice(item));
        }

        const id = Number(item?.id);
        if (!id) return;

        const priceRes = await fetch(
          `${API_BASE}/api/prebooking/check-availability?item_id=${id}`,
          { headers: { Accept: "application/json" } }
        );
        const priceData = await priceRes.json();
        const prebook = Number(priceData?.data?.prebooking_price);
        if (!cancelled && prebook > 0) setPrebookingPrice(prebook);
      } catch {
        // Silently fail — callers fall back to their static values.
      }
    };

    fetchPricing();
    return () => {
      cancelled = true;
    };
  }, []);

  const discountAmount =
    mrp != null && itemCost != null ? Math.max(mrp - itemCost, 0) : 0;

  const remaining =
    itemCost != null && prebookingPrice != null
      ? itemCost - prebookingPrice
      : null;

  return { itemCost, mrp, discountAmount, prebookingPrice, remaining };
}

/** Formats a rupee amount in the Indian numbering system, e.g. 2999 -> "2,999". */
export function formatINR(amount: number) {
  return amount.toLocaleString("en-IN");
}
